import {createContext, useContext} from 'react'
import {PeriodType} from '../../../global'

export interface NaturalReferencingKeywordItem {
    keyword: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
}

export interface NaturalReferencingKeywordDataArray extends NaturalReferencingKeywordItem {
    date: string;
}

export type KeywordDataRecord = Record<string, NaturalReferencingKeywordItem>;


export interface NaturalReferencingMetrics {
    users: string;
    sessions: string;
    keyword: string
}

export interface NaturalReferencingMetricsWithPosition extends NaturalReferencingMetrics {
    position: number;
    trend: string;
}

export interface NaturalReferencingGeographyMetrics extends NaturalReferencingMetrics {
    city?: string;
    country?: string;
}

export interface NaturalReferencingPageMetrics extends NaturalReferencingMetrics {
    path: string;
    page: string;
}

export interface NaturalReferencingBySearchEngine {
    pageviews: string;
    avgSessionDuration: string;
    socialNetwork: string;
    sessions: string;
}

export interface NaturalReferencingAcquisition {
    parCanaux: NaturalReferencingMetrics[];
    parMotCles: NaturalReferencingMetricsWithPosition[];
    parMoteurRecherche: NaturalReferencingBySearchEngine[];
    parReseauxSociaux: NaturalReferencingBySearchEngine[];
}

export interface NaturalReferencingContent {
    pagesPlusConsultees: NaturalReferencingPageMetrics[];
    pagesSortie: any[];
}

export interface NaturalReferencingVueAudience {
    nombreVisites: string;
    nombrePagesVues: string;
    dureeMoyenneSessions: string;
    nombrePagesVuesVisiteurs: string;
    nombreVisiteurRestent: number;
}

export interface NaturalReferencingViewByGeography {
    origineGeographiqueVille: NaturalReferencingGeographyMetrics[];
    origineGeographiquePays: NaturalReferencingGeographyMetrics[];
}

export interface NaturalReferencingAuditCategory {
    id: string;
    title: string;
    score: number;
    description?: string;
    manualDescription?: string;
    auditRefs: any[];
}

export interface NaturalReferencingLighthouseResultCategories {
    performance: NaturalReferencingAuditCategory;
    accessibility: NaturalReferencingAuditCategory;
    'best-practices': NaturalReferencingAuditCategory;
    seo: NaturalReferencingAuditCategory;
}

export interface NaturalReferencingLighthouseResult {
    categories: NaturalReferencingLighthouseResultCategories
}

export interface NaturalReferencingDeviceData {
    lighthouseResult: NaturalReferencingLighthouseResult;
    error?: boolean;
    message?: string;
    hasError: boolean;
}

export type NaturalReferencingPageSpeedDataResponse = {
    mobile: NaturalReferencingDeviceData
    desktop: NaturalReferencingDeviceData
    date: string
};

export type NaturalReferencingPageSpeedResponse = {
    pageSpeedData: NaturalReferencingPageSpeedDataResponse
}

export interface NaturalReferencingDataState {
    acquisition: NaturalReferencingAcquisition
    vueAudience: NaturalReferencingVueAudience
    contenu: NaturalReferencingContent
    vueParGeographie: NaturalReferencingViewByGeography
}

export interface NaturalReferencingInterface {
    acquisition: NaturalReferencingAcquisition,
    audienceView: NaturalReferencingVueAudience,
    content: NaturalReferencingContent,
    viewByGeography: NaturalReferencingViewByGeography,
    period: PeriodType
}

export const initialValuesNaturalReferencingPageSpeedResponse: NaturalReferencingPageSpeedDataResponse = {
    mobile: {
        lighthouseResult: {
            categories: {
                performance: {
                    id: '',
                    title: '',
                    score: 0,
                    auditRefs: [],
                },
                accessibility: {
                    id: '',
                    title: '',
                    description: '',
                    score: 0,
                    manualDescription: '',
                    auditRefs: [],
                },
                'best-practices': {
                    id: '',
                    title: '',
                    score: 0,
                    auditRefs: [],
                },
                seo: {
                    id: '',
                    title: '',
                    description: '',
                    score: 0,
                    manualDescription: '',
                    auditRefs: [],
                },
            },
        },
        error: false,
        hasError: false,
        message: '',
    },
    desktop: {
        lighthouseResult: {
            categories: {
                performance: {
                    id: '',
                    title: '',
                    score: 0,
                    auditRefs: [],
                },
                accessibility: {
                    id: '',
                    title: '',
                    description: '',
                    score: 0,
                    manualDescription: '',
                    auditRefs: [],
                },
                'best-practices': {
                    id: '',
                    title: '',
                    score: 0,
                    auditRefs: [],
                },
                seo: {
                    id: '',
                    title: '',
                    description: '',
                    score: 0,
                    manualDescription: '',
                    auditRefs: [],
                },
            },
        },
        error: false,
        hasError: false,
        message: '',
    },
    date: '',
}

export const initialValuesNaturalReferencingAcquisition: NaturalReferencingAcquisition = {
    parCanaux: [],
    parMotCles: [],
    parMoteurRecherche: [],
    parReseauxSociaux: [],
}

export const initialValuesNaturalReferencingContent: NaturalReferencingContent = {
    pagesPlusConsultees: [],
    pagesSortie: [],
}

export const initialValuesNaturalReferencingAudienceView = {
    nombreVisites: '',
    nombrePagesVues: '',
    dureeMoyenneSessions: '',
    nombrePagesVuesVisiteurs: '',
    nombreVisiteurRestent: 0,
}

export const initialValuesNaturalReferencingViewByGeography: NaturalReferencingViewByGeography = {
    origineGeographiqueVille: [],
    origineGeographiquePays: [],
}

export const NaturalReferencingContext = createContext<NaturalReferencingInterface>({
    acquisition: initialValuesNaturalReferencingAcquisition,
    content: initialValuesNaturalReferencingContent,
    audienceView: initialValuesNaturalReferencingAudienceView,
    viewByGeography: initialValuesNaturalReferencingViewByGeography,
    period: {start: '', end: ''}
})

export const useNaturalReferencing = () => {

    const context = useContext(NaturalReferencingContext)
    if (!context) throw new Error('useNaturalReferencingData must be used within NaturalReferencingProvider')
    return context
}